import { EMOJI_DISPLAY, tagLabelClass } from "../state";
import type { Slot, SlotTag } from "../state";
import Tooltip from "./Tooltip";

function totalVotes(counts: Record<string, number>): number {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

function top2Votes(counts: Record<string, number>): [string, number][] {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2);
}

interface CalendarDayProps {
  dateStr: string;
  dayNum: number;
  slots: Slot[];
  isSelected?: boolean;
  isOtherMonth?: boolean;
  onDayClick: (dateStr: string) => void;
  /** slot_id -> username -> emoji (for "who voted" tooltip). */
  reactionsByUserBySlot?: Record<number, Record<string, string>>;
  /** Delay in ms before showing day tooltip. */
  tooltipDelayMs?: number;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarDay({
  dateStr,
  dayNum,
  slots = [],
  isSelected,
  isOtherMonth = false,
  onDayClick,
  reactionsByUserBySlot = {},
  tooltipDelayMs = 400,
}: CalendarDayProps) {
  const isEmpty = !dateStr;
  const today = todayStr();
  const isToday = dateStr === today;
  const isPast = dateStr && dateStr < today;
  const weekday = dateStr ? new Date(dateStr + "T12:00:00").getDay() : -1;
  const isWeekend = weekday === 0 || weekday === 6;

  const slotsByVotes = [...slots].sort(
    (a, b) =>
      totalVotes(b.reaction_counts || {}) - totalVotes(a.reaction_counts || {}),
  );

  const allTags: SlotTag[] = [];
  const seen = new Set<number>();
  for (const slot of slots) {
    for (const t of slot.tags || []) {
      if (!seen.has(t.tag_id)) {
        seen.add(t.tag_id);
        allTags.push(t);
      }
    }
  }

  let dayVariantClass = "bg-white dark:bg-gray-950";
  if (isOtherMonth) {
    dayVariantClass =
      "bg-white dark:bg-gray-950 text-gray-400 dark:text-gray-500 opacity-60";
  } else if (isToday) {
    dayVariantClass = "bg-brutal-orange dark:bg-brutal-orange-dark";
  } else if (isPast) {
    dayVariantClass = isWeekend
      ? "bg-purple-100/80 dark:bg-brutal-purple-dark"
      : "bg-gray-100 dark:bg-gray-900/70";
  } else if (isWeekend) {
    dayVariantClass = "bg-purple-100 dark:bg-brutal-purple";
  }

  let dayNumberClass =
    "font-semibold text-brutal-purple dark:text-brutal-orange";
  if (isOtherMonth) {
    dayNumberClass =
      "font-semibold text-gray-400 dark:text-gray-500";
  } else if (isToday || isSelected) {
    dayNumberClass =
      "font-semibold text-black dark:text-gray-900";
  } else if (isPast && !isWeekend) {
    dayNumberClass =
      "font-semibold text-gray-600 dark:text-gray-300";
  } else if (isPast && isWeekend) {
    dayNumberClass =
      "font-semibold text-black dark:text-gray-50";
  }

  const base =
    "aspect-square max-h-20 flex flex-col items-center justify-center rounded-none cursor-pointer transition-colors relative border border-black dark:border-gray-700";
  const emptyClass = isEmpty ? "invisible cursor-default" : "";
  const hoverClass = !isEmpty
    ? isOtherMonth
      ? "hover:bg-gray-100 dark:hover:bg-gray-900"
      : "hover:bg-orange-100 dark:hover:bg-brutal-purple-dark"
    : "";
  const selectedClass = isSelected
    ? "!bg-purple-100 dark:!bg-brutal-purple-dark"
    : "";

  const dayTooltipContent =
    slotsByVotes.length > 0 ? (
      <div className="flex flex-col gap-1.5">
        {slotsByVotes.map((slot) => {
          const whoVoted = reactionsByUserBySlot[slot.id];
          const firstLine = (slot.description || "").split("\n")[0].trim() || "(no description)";
          const hasVoters = whoVoted && Object.keys(whoVoted).length > 0;
          return (
            <div key={slot.id} className="flex flex-col gap-0.5">
              <span className="whitespace-nowrap">{slot.start_time} · {firstLine}</span>
              {hasVoters ? (
                <div className="flex flex-col">
                  {Object.entries(whoVoted!).map(([u, emoji]) => (
                    <span key={`${u}-${emoji}`} className="whitespace-nowrap">
                      {EMOJI_DISPLAY[emoji] ?? emoji} {u}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    ) : null;

  const dayCell = (
    <div
      className={`${base} ${dayVariantClass} ${emptyClass} ${hoverClass} ${selectedClass}`}
      data-date={dateStr}
      onClick={() => !isEmpty && onDayClick(dateStr)}
      role={isEmpty ? undefined : "button"}
      tabIndex={isEmpty ? undefined : 0}
      onKeyDown={(e) =>
        !isEmpty && (e.key === "Enter" || e.key === " ") && onDayClick(dateStr)
      }
    >
      {allTags.length > 0 ? (
        <div className="absolute top-0 left-0 flex flex-wrap gap-0.5 p-0.5 max-w-full">
          {allTags.map((t) => (
            <span
              key={t.tag_id}
              className={`inline-block px-1 py-0.5 text-[10px] font-logo font-bold uppercase rounded-sm -rotate-12 origin-left shadow-sm border-2 border-black dark:border-gray-100 ${tagLabelClass(t.tag_name)}`}
            >
              {t.tag_name}
            </span>
          ))}
        </div>
      ) : null}
      {dayNum ? (
        <span
          className={dayNumberClass}
        >
          {dayNum}
        </span>
      ) : null}
      {slotsByVotes.length > 0 ? (
        <div className="flex flex-col gap-0.5 mt-1 w-full font-mono text-[10px] leading-tight px-1 min-w-0">
          {slotsByVotes.slice(0, 3).map((slot) => {
            const top2 = top2Votes(slot.reaction_counts || {});
            const votesStr = top2
              .map(([emoji, n]) => `${EMOJI_DISPLAY[emoji] || emoji}${n}`)
              .join(" ");
            return (
              <div
                key={slot.id}
                className="flex w-full justify-between items-baseline gap-1 truncate min-w-0"
              >
                <span className="truncate min-w-0">{slot.start_time}</span>
                {votesStr ? (
                  <span className="shrink-0 tabular-nums">{votesStr}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  if (!isEmpty && dayTooltipContent) {
    return (
      <Tooltip content={dayTooltipContent} delayMs={tooltipDelayMs} multiline className="block w-full h-full">
        {dayCell}
      </Tooltip>
    );
  }
  return dayCell;
}
